(ns shared.core.components.recharts.sankey-chart
  (:require [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.components.recharts.tooltip :as tooltip]
            [shared.core.util :as util]))

(defn- label-margins
  "Left/right margins sized to fit the widest source and target labels."
  [{:keys [nodes source-label-gap target-label-gap]}]
  (let [font (str style/font-regular-weight " 12px " style/roboto-font-family)
        nodes (map util/->kebab-case-keywords nodes)
        buffer 8
        widest (fn [pred]
                 (->> nodes
                      (filter pred)
                      (map #(recharts.util/measure-text-width (:name %) font))
                      (reduce max 0)))
        source? #(boolean (:is-source %))]
    {:left (+ (widest source?) source-label-gap buffer)
     :right (+ (widest (complement source?)) target-label-gap buffer)}))

(defn- customized-sankey-node
  "Custom node with hover opacity behavior."
  [{:keys [props on-node-enter on-node-leave active-node active-link links source-label-gap target-label-gap]}]
  (let [{:keys [x y width height index payload]} (js->clj props :keywordize-keys true)
        my-index (:originalIndex payload)
        is-source (boolean (:isSource payload))
        final-color (or (:color payload) "#999999")
        stroke-color (or (:stroke payload) (style/alc-color "Background/card_hex"))
        stroke-width (:strokeWidth payload)
        node-width (or (:width payload) width)
        adjusted-x (if (and is-source (< node-width width))
                     (+ x (- width node-width))
                     x)
        name (:name payload)
        gap (if is-source source-label-gap target-label-gap)
        text-x (if is-source
                 (- adjusted-x gap)
                 (+ adjusted-x node-width gap))
        text-anchor (or (:anchor payload) (if is-source "end" "start"))
        linked? (cond
                  (and (some? active-link)
                       (map? active-link))
                  (let [{:keys [source-index target-index]} active-link]
                    (or (= my-index source-index)
                        (= my-index target-index)))

                  (some? active-node)
                  (let [touches (some (fn [{:keys [source target]}]
                                        (or (and (= source active-node)
                                                 (= target my-index))
                                            (and (= target active-node)
                                                 (= source my-index))))
                                      (or links []))]
                    (or (= my-index active-node) touches))

                  :else true)
        opacity (if (and (nil? active-node) (nil? active-link))
                  1.0
                  (if linked? 1.0 0.4))
        handle-enter #(when on-node-enter (on-node-enter payload))
        handle-leave #(when on-node-leave (on-node-leave))]
    (re/as-element
      [recharts.util/re-layer {:key (str "CustomNode" index)
                               :onMouseEnter handle-enter
                               :onMouseLeave handle-leave
                               :opacity opacity}
       [recharts.util/re-rectangle {:x adjusted-x
                                    :y y
                                    :width node-width
                                    :height height
                                    :fill final-color
                                    :stroke stroke-color
                                    :strokeWidth (or stroke-width 0)
                                    :fillOpacity 1}]
       [:text {:x text-x
               :y (+ y (/ height 2))
               :text-anchor text-anchor
               :font-size 12
               :font-family style/roboto-font-family
               :font-weight style/font-regular-weight
               :alignment-baseline "middle"
               :dominant-baseline "middle"
               :fill (style/alc-color "Text/primary")
               :style {:user-select "none"}}
        name]])))

(defn- customized-sankey-link
  "Custom link path using cubic bezier with hover opacity behavior."
  [{:keys [props active-node active-link on-link-enter on-link-leave on-click on-double-click] :as args}]
  (let [click-timer* (atom nil)
        {:keys [sourceX targetX sourceY targetY sourceControlX targetControlX linkWidth index payload]
         :as clj-props}
        (js->clj props :keywordize-keys true)
        path (str "M" sourceX "," sourceY
                  " C" sourceControlX "," sourceY
                  " " targetControlX "," targetY
                  " " targetX "," targetY)
        source (:source payload)
        target (:target payload)
        s (if (map? source)
            (:originalIndex source)
            source)
        t (if (map? target)
            (:originalIndex target)
            target)
        hovered? (and (map? active-link)
                      (= s (:source-index active-link))
                      (= t (:target-index active-link)))
        node-hover? (and (some? active-node) (nil? active-link))
        connected? (and node-hover?
                        (or (= s active-node)
                            (= t active-node)))
        opacity (cond
                  node-hover? (if connected? 0.6 0.1)
                  (nil? active-link) 0.3
                  hovered? 0.6
                  :else 0.1)
        stroke-color (or (get-in payload [:source :color])
                         (when (map? source) (:color source))
                         (get-in payload [:target :color])
                         (when (map? target) (:color target))
                         (:color payload)
                         "#999999")
        raw-width (or linkWidth (max 1 (/ (or (:value payload) 1) 10)))
        width (max 0.5 raw-width)
        handle-enter #(when (and on-link-enter (number? s) (number? t))
                        (on-link-enter {:source-index s
                                        :target-index t}))
        handle-leave #(when on-link-leave (on-link-leave))]
    (re/as-element
      [:path {:key (str "CustomLink" index)
              :d path
              :stroke stroke-color
              :strokeWidth width
              :strokeLinecap "butt"
              :fill "none"
              :opacity opacity
              :onClick (if (and on-click on-double-click)
                         #(do
                            (when @click-timer* (js/clearTimeout @click-timer*))
                            (reset! click-timer* (js/setTimeout (fn [] (on-click % clj-props)) 250)))
                         on-click)
              :onDoubleClick #(when on-double-click
                                (when @click-timer*
                                  (js/clearTimeout @click-timer*)
                                  (reset! click-timer* nil))
                                (on-double-click % clj-props)
                                (util/stop-event %))
              :onMouseEnter handle-enter
              :onMouseLeave handle-leave}])))

(defn- format-tooltip-value
  [value]
  (cond
    (number? value) (util/readable-number value)
    (string? value) value
    (some? value) (str value)
    :else "—"))

(defn- percent-of
  [value total]
  (let [total (or total 0)
        value (or value 0)]
    (when (pos? total)
      (let [pct (* 100 (/ value total))]
        (str (.toFixed pct 1) "%")))))

(defn- sankey-tooltip-content
  [{:keys [nodes links sort-by-value? custom-sort limit style-map value-transf-fn show-percent?
           show-double-click-message?]
    :or {show-percent? true}} props]
  (let [{:keys [active payload]} (js->clj props :keywordize-keys true)]
    (when (and active (seq payload))
      (let [nodes (mapv util/->camelCaseKeywords nodes)
            links (mapv util/->camelCaseKeywords links)
            entry (-> (first payload) (util/->camelCaseKeywords))
            outer-payload (:payload entry)
            inner-payload (when (map? outer-payload) (:payload outer-payload))
            item (or inner-payload outer-payload {})
            safe-node (fn [idx]
                        (when (and (number? idx)
                                   (<= 0 idx)
                                   (< idx (count nodes)))
                          (nth nodes idx)))
            source (:source item)
            target (:target item)
            link? (and source target)
            source-index (when link?
                           (if (map? source)
                             (:originalIndex source)
                             source))
            target-index (when link?
                           (if (map? target)
                             (:originalIndex target)
                             target))
            hover-value (when link?
                          (or (:value item) (:value outer-payload)))
            source-node (when (some? source-index) (safe-node source-index))
            active-entry (cond
                           (and link? source-node) source-node
                           :else item)
            node-entry (or active-entry {})
            header-description-fn (:headerDescriptionFn node-entry)
            node-index (:originalIndex node-entry)
            node-name (:name node-entry)
            node-hover? (not link?)
            node-is-source? (true? (:isSource node-entry))
            source-node-hover? (and node-hover? node-is-source?)
            target-node-hover? (and node-hover? (not node-is-source?))
            link-hover? link?
            show-indicator? (not (or source-node-hover? link-hover?))
            incoming-links (->> links
                                (filter #(= (:target %) node-index))
                                vec)
            outgoing-links (->> links
                                (filter #(= (:source %) node-index))
                                vec)
            incoming-total (reduce (fn [sum {:keys [value]}]
                                     (+ sum (or value 0)))
                                   0
                                   incoming-links)
            outgoing-total (reduce (fn [sum {:keys [value]}]
                                     (+ sum (or value 0)))
                                   0
                                   outgoing-links)
            incoming-label (fn [neighbor idx]
                             (let [neighbor-name (or (:name neighbor)
                                                     (str "Incoming #" (inc idx)))]
                               (if (and target-node-hover? node-hover?)
                                 neighbor-name
                                 (str neighbor-name " → " (or node-name "")))))
            outgoing-label (fn [neighbor idx]
                             (let [neighbor-name (or (:name neighbor)
                                                     (str "Outgoing #" (inc idx)))]
                               (if (or (and source-node-hover? node-hover?)
                                       link-hover?)
                                 neighbor-name
                                 (str (or node-name "") " → " neighbor-name))))
            incoming-rows (vec
                            (map-indexed
                              (fn [idx link]
                                (let [src (safe-node (:source link))
                                      value (or (:value link) 0)
                                      percent (percent-of value incoming-total)
                                      color (or (:color src)
                                                (:color node-entry)

                                                "#64748B")]
                                  {:key (str "incoming-" idx)
                                   :label (incoming-label src idx)
                                   :value (format-tooltip-value value)
                                   :percent (or percent "")
                                   :color color
                                   :indicator (when show-indicator?
                                                {:shape :square
                                                 :color color})
                                   :highlight? false}))
                              incoming-links))
            outgoing-rows (vec
                            (map-indexed
                              (fn [idx link]
                                (let [tgt (safe-node (:target link))
                                      value (or (:value link) 0)
                                      percent (percent-of value outgoing-total)
                                      hovered (and tgt
                                                   (= (:originalIndex tgt) target-index)
                                                   (= value hover-value))
                                      color (or (:color node-entry)
                                                (:color tgt)
                                                "#1F2937")]
                                  {:key (str "outgoing-" idx)
                                   :label (outgoing-label tgt idx)
                                   :value (format-tooltip-value value)
                                   :percent percent
                                   :color color
                                   :indicator (when show-indicator?
                                                {:shape :square
                                                 :color color})
                                   :highlight? hovered}))
                              outgoing-links))
            rows (->> (concat incoming-rows outgoing-rows)
                      (into []))]
        (when (and node-name (seq rows))
          (re/as-element
            [tooltip/tooltip-panel {:style-map (merge style-map
                                                      {:max-height "100%"
                                                       :min-width 240})
                                    :numeric-align :right
                                    :column-template "minmax(160px,1fr) 68px"
                                    :header {:label node-name
                                             :description (when header-description-fn
                                                            (header-description-fn node-entry))}
                                    :column-count (js/Math.ceil (/ (or limit (count rows)) 20))
                                    :sort-by-value? sort-by-value?
                                    :custom-sort custom-sort
                                    :limit limit
                                    :rows rows
                                    :value-transf-fn value-transf-fn
                                    :show-percent? show-percent?
                                    :show-double-click-message? show-double-click-message?}]))))))

(defn- sankey-tooltip
  [data]
  (fn [props]
    (sankey-tooltip-content data props)))

(defn sankey-chart
  "Reusable Sankey chart.
   nodes: [{:name \"Sunday\" :isSource true :color \"#...\"} ...]
   links: [{:source 0 :target 8 :value 10} ...]"
  [_]
  (let [mouse-position* (re/ratom {:x 0 :y 0})
        active-node* (re/ratom nil)
        active-link* (re/ratom nil)]
    (fn [{:keys [height node-padding margin dynamic-margins? nodes links tooltip-config node-config link-config
                 source-label-gap target-label-gap min-width]
          :or {node-padding 40
               margin {:left 100 :right 100 :top 40 :bottom 40}
               dynamic-margins? true
               source-label-gap 8
               target-label-gap 18
               min-width 600}
          :as args}]
      (when (seq nodes)
        (let [height (or height (recharts.util/sankey-chart-height args))
              margin (cond-> margin
                             dynamic-margins? (merge (label-margins {:nodes nodes
                                                                     :source-label-gap source-label-gap
                                                                     :target-label-gap target-label-gap})))
              nodes (mapv (fn [{:keys [color] :as node}]
                            (assoc node :color (or color (some-> node
                                                                 (util/->kebab-case-keywords)
                                                                 :name
                                                                 (recharts.util/color-from-string)))))
                          nodes)
              links* (vec (or links []))
              nodes-with-indexes (map-indexed (fn [idx node]
                                                (assoc node :originalIndex idx))
                                              nodes)
              links-with-indexes (map-indexed (fn [idx link]
                                                (assoc link :originalIndex idx))
                                              links*)
              data-with-indexes {:nodes nodes-with-indexes
                                 :links links-with-indexes}
              data-js #js {:nodes (clj->js nodes-with-indexes)
                           :links (clj->js links-with-indexes)}
              current-node @active-node*
              current-link @active-link*
              handle-node-enter (fn [node]
                                  (when-let [idx (:originalIndex node)]
                                    (reset! active-node* idx)
                                    (reset! active-link* nil)))
              handle-node-leave #(reset! active-node* nil)
              handle-link-enter (fn [{:keys [source-index target-index]}]
                                  (when (and (number? source-index)
                                             (number? target-index))
                                    (reset! active-link* {:source-index source-index
                                                          :target-index target-index})
                                    (reset! active-node* nil)))
              handle-link-leave #(reset! active-link* nil)
              {:keys [x y]} @mouse-position*]
          [:div {:style {:width "100%"
                         :overflow-x :auto
                         :overflow-y :hidden}}
           [:div {:style {:width "100%"
                          :min-width min-width
                          :height height
                          :font-family style/roboto-font-family
                          :position :relative}
                  :on-mouse-move (fn [e]
                                   (reset! mouse-position* {:x (.-clientX e)
                                                            :y (.-clientY e)}))}
            [recharts.util/re-responsive-container {:width "100%" :height height}
             [recharts.util/re-sankey {:data data-js
                                       :nodeWidth 12
                                       :nodePadding node-padding
                                       :margin (clj->js margin)
                                       :sort false
                                       :linkCurvature 0.5
                                       :node (fn [p]
                                               (re/as-element
                                                 [customized-sankey-node (merge {:props p
                                                                                 :on-node-enter handle-node-enter
                                                                                 :on-node-leave handle-node-leave
                                                                                 :active-node current-node
                                                                                 :active-link current-link
                                                                                 :links (:links data-with-indexes)
                                                                                 :source-label-gap source-label-gap
                                                                                 :target-label-gap target-label-gap}
                                                                                node-config)]))
                                       :link (fn [p]
                                               (re/as-element
                                                 [customized-sankey-link (merge {:props p
                                                                                 :active-node current-node
                                                                                 :active-link current-link
                                                                                 :on-link-enter handle-link-enter
                                                                                 :on-link-leave handle-link-leave}
                                                                                link-config)]))}
              [recharts.util/re-tooltip {:content (sankey-tooltip (merge tooltip-config
                                                                         data-with-indexes
                                                                         {:style-map (tooltip/floating-style x y)}
                                                                         (when-not @active-link*
                                                                           ;; Double click currently only supported on links
                                                                           {:show-double-click-message? false})))
                                         :isAnimationActive false
                                         :cursor {:stroke "#CBD5E1"
                                                  :strokeWidth 1
                                                  :fill "rgba(148, 163, 184, 0.08)"}}]]]]])))))